import classNames from 'classnames';
import { FormikErrors } from 'formik';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { BiSolidPencil } from 'react-icons/bi';

import UploadVideoButton from '../upload-video/UploadVideoButton';
import Error from './Error';

import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

export enum UploadAspectRatios {
  OneToOne = 1,
  FourToFive = 4 / 5,
  ThreeToTwo = 3 / 2,
  NineToSixteen = 9 / 16
}

const UploadVideo = ({
  preview,
  onClick,
  videoUrl,
  label,
  maxLabel,
  aspectRatio,
  disabled,
  buttonStyle = {},
  defaultIconSize
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t } = useTranslation(['events']);
  useEffect(() => {
    // video autostarts and we stop it to also show first frame
    videoRef.current?.pause?.();
    // TODO: handle sorce attachement here smth like
    // videoRef.current?.src = preview || videoUrl;
  }, [videoRef.current]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-full"
      disabled={disabled}
    >
      {preview || videoUrl ? (
        <>
          <div
            className={classNames(
              'h-[350px] w-full absolute bottom-0',
              styles.videoUploaderGradient
            )}
          />
          <video
            ref={videoRef}
            loop
            playsInline
            autoPlay
            muted
            className="w-full h-full object-cover object-center"
          >
            <source src={preview || videoUrl} type="video/mp4" />
          </video>
        </>
      ) : (
        <div
          className="md:justify-center w-full h-full bg-neutral-100 text-center flex flex-col items-center pt-32 md:pt-0 relative"
          style={buttonStyle}
        >
          <div
            className={classNames(
              'h-[350px] w-full absolute bottom-0',
              styles.videoUploaderGradient
            )}
          />
          <p
            className={classNames(
              !defaultIconSize &&
                aspectRatio === UploadAspectRatios.OneToOne &&
                'text-3xl',
              !defaultIconSize &&
                aspectRatio !== UploadAspectRatios.OneToOne &&
                'text-9xl md:-mt-[40%]'
            )}
            style={defaultIconSize ? { fontSize: defaultIconSize } : {}}
          >
            🎥
          </p>
          <p
            className={classNames(
              'text-sm decoration-[#575757] font-light',
              aspectRatio === UploadAspectRatios.OneToOne
                ? 'mt-0 w-full text-xs'
                : 'w-[140px] mt-0',
              buttonStyle && 'w-full mt-1'
            )}
          >
            {label || '9:16 • 100 Megabytes'}
          </p>
          {maxLabel !== null && (
            <p className="text-sm decoration-[#575757] font-semibold">
              {maxLabel || t('events:host.hypeVideoOptional')}
            </p>
          )}
        </div>
      )}
    </button>
  );
};

interface VideoUploadProps {
  label?: string;
  maxLabel?: string;
  className?: string;
  aspectRatio?: UploadAspectRatios;
  onUploadVideo: (file: File) => void;
  videoUrl?: string;
  disabled?: boolean;
  isUploading?: boolean;
  editIconOnly?: boolean;
  error?: string | FormikErrors<any> | string[] | FormikErrors<any>[];
  buttonStyle?: CSSProperties;
  defaultIconSize?: number;
  onSetError?: (error: string) => void;
}

const EditVideoIcon = ({ onClick }: { onClick?: () => void }) => (
  <div className="absolute right-0">
    <div
      className="mr-2 mt-2 rounded-full p-1 bg-blue-200 w-[32px] h-[32px] flex items-center justify-center"
      style={{ backgroundColor: '#EEF1FF' }}
      onClick={() => onClick?.()}
    >
      <BiSolidPencil size={18} color="#3853C4" />
    </div>
  </div>
);

const VideoUpload: React.FC<VideoUploadProps> = ({
  label,
  maxLabel,
  className,
  aspectRatio = UploadAspectRatios.NineToSixteen,
  onUploadVideo,
  isUploading,
  videoUrl,
  error,
  disabled,
  buttonStyle,
  defaultIconSize,
  editIconOnly,
  onSetError
}) => {
  const [preview, setPreview] = useState<string>('');

  const onUploadVideoButtonClicked = (blob: string, file) => {
    setPreview(blob);
    onUploadVideo(file);
  };

  const WrappedButton = useCallback(
    ({ onClick }) => (
      <UploadVideo
        onClick={onClick}
        videoUrl={videoUrl}
        preview={preview}
        maxLabel={maxLabel}
        disabled={disabled}
        aspectRatio={aspectRatio}
        label={label}
        buttonStyle={buttonStyle}
        defaultIconSize={defaultIconSize}
      />
    ),
    [preview, videoUrl, disabled, buttonStyle, defaultIconSize]
  );

  return (
    <>
      <Error error={error} />
      <div
        className={classNames(className, {
          'animate-pulse': isUploading
        })}
      >
        <div className="md:mx-auto relative">
          {videoUrl && !editIconOnly && (
            <UploadVideoButton
              preview={preview}
              setPreview={setPreview}
              cropShape="rect"
              aspectRatio={aspectRatio}
              ButtonComponent={EditVideoIcon}
              uploadVideo={onUploadVideoButtonClicked}
            />
          )}

          {videoUrl && editIconOnly && <EditVideoIcon />}

          {(!videoUrl || (videoUrl && !editIconOnly)) && (
            <div
              className={classNames(
                'overflow-hidden w-full mx-auto max-h-[850px] md:max-h-[800px]',
                {
                  'border-1 border-red-700': !!error
                },
                aspectRatio === UploadAspectRatios.OneToOne && 'rounded-2xl',
                aspectRatio === UploadAspectRatios.OneToOne &&
                  !error &&
                  'border-white border-2',
                aspectRatio === UploadAspectRatios.OneToOne &&
                  !!error &&
                  'border-red-700 border-2'
              )}
              style={
                buttonStyle || {
                  aspectRatio: aspectRatio.toString()
                }
              }
            >
              <UploadVideoButton
                preview={preview}
                setPreview={setPreview}
                cropShape="rect"
                aspectRatio={aspectRatio}
                ButtonComponent={WrappedButton}
                uploadVideo={onUploadVideoButtonClicked}
                onSetError={onSetError}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoUpload;
